// ============================================================
// System Dashboard Builder Page — Drag-and-Drop Widget Editor
// Route: /dashboard-templates/:id (edit) or /dashboard-templates/new (create)
// ============================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { Save, Play, ArrowLeft, Plus, Trash2, GripVertical, BarChart3, Table2, MapPin, Calendar, Hash, FileText, Gauge, List, Image, Filter, Minus, Maximize2, RefreshCw, X, TrendingUp } from 'lucide-react';
import RGL from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ReactGridLayout = RGL as any;
import type { ReportDefinitionLocal, DashboardConfig, DashboardWidget } from '@firesquirrel/local-db';
import { getReportDefinition, updateReportDefinition, createReportDefinition } from '../../services/systemReportDefinitionService';
import { WidgetRenderer, type FilterValue } from '@firesquirrel/ui-admin';

type WidgetType = DashboardWidget['type'];

interface WidgetTypeOption {
  type: WidgetType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const WIDGET_TYPES: WidgetTypeOption[] = [
  { type: 'kpi', label: 'KPI Card', icon: Hash, description: 'Single metric + trend indicator' },
  { type: 'chart', label: 'Chart', icon: BarChart3, description: 'Bar, line, pie, area, radar, etc.' },
  { type: 'table', label: 'Data Table', icon: Table2, description: 'Mini tabular grid, sortable' },
  { type: 'text', label: 'Text Block', icon: FileText, description: 'Markdown text block' },
  { type: 'filter', label: 'Global Filter', icon: Filter, description: 'Filter affecting all widgets' },
  { type: 'list', label: 'Activity Feed', icon: List, description: 'Icon + text rows (recent items)' },
  { type: 'progressBar', label: 'Progress Bar', icon: TrendingUp, description: 'Target tracking bar' },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Visual separator' },
  { type: 'image', label: 'Image / Logo', icon: Image, description: 'Static image from URL' },
  { type: 'map', label: 'Map (T2+)', icon: MapPin, description: 'Geographic pins/heatmap' },
  { type: 'gauge', label: 'Gauge (T2+)', icon: Gauge, description: 'Progress/percentage dial' },
  { type: 'calendar', label: 'Calendar (T2+)', icon: Calendar, description: 'Mini calendar or upcoming' },
];

const DEFAULT_DASHBOARD_CONFIG: DashboardConfig = {
  layout: { columns: 12, rowHeight: 80 },
  widgets: [],
  autoRefreshSeconds: 0,
};

function WidgetPalette({ onAddWidget }: { onAddWidget: (type: WidgetType) => void }) {
  return (
    <div className="p-3 space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 text-gray-400">
        Add Widget
      </h3>
      <div className="grid grid-cols-2 gap-2">
        {WIDGET_TYPES.map((wt) => {
          const Icon = wt.icon;
          return (
            <button
              key={wt.type}
              onClick={() => onAddWidget(wt.type)}
              className="flex flex-col items-center gap-1 p-3 rounded-lg border border-gray-300 hover:border-blue-400 hover:bg-gray-100 transition-colors text-center"
            >
              <Icon className="h-5 w-5 text-gray-600" />
              <span className="text-xs text-gray-600">{wt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WidgetPropertiesPanel({
  widget,
  onUpdate,
}: {
  widget: DashboardWidget | null;
  onUpdate: (updates: Partial<DashboardWidget>) => void;
}) {
  if (!widget) {
    return (
      <div className="p-4 text-center text-gray-400">
        <p className="text-sm">Select a widget to edit its properties</p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
        Widget Properties
      </h3>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-600">Title</label>
          <input
            type="text"
            value={widget.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-600">Data Source</label>
          <select
            value={widget.dataSource?.cubeId || ''}
            onChange={(e) => onUpdate({ dataSource: e.target.value ? { cubeId: e.target.value, measures: [], dimensions: [], filters: [] } : undefined })}
            className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Select data source...</option>
            <option value="entities">Entities</option>
            <option value="inspections">Inspections</option>
            <option value="cases">Cases</option>
            <option value="violations">Violations</option>
            <option value="permits">Permits</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Width (cols)</label>
            <input
              type="number"
              min={1}
              max={12}
              value={widget.layout.w}
              onChange={(e) => onUpdate({ layout: { ...widget.layout, w: parseInt(e.target.value) || 1 } })}
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Height (rows)</label>
            <input
              type="number"
              min={1}
              max={6}
              value={widget.layout.h}
              onChange={(e) => onUpdate({ layout: { ...widget.layout, h: parseInt(e.target.value) || 1 } })}
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface SystemDashboardBuilderPageProps {
  mode?: 'edit' | 'view' | 'fullscreen';
}

export default function SystemDashboardBuilderPage({ mode = 'edit' }: SystemDashboardBuilderPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumbLabel } = useBreadcrumbs();
  const isNew = !id || id === 'new';
  const isViewMode = mode === 'view' || mode === 'fullscreen';
  const isFullscreen = mode === 'fullscreen';

  // Detect if this is an AI-generated dashboard from navigation state
  const aiDashboardData = location.state as { aiGenerated?: boolean; previewConfig?: any } | null;
  const [isAIGenerated, setIsAIGenerated] = useState<boolean>(aiDashboardData?.aiGenerated || false);

  const [dashboard, setDashboard] = useState<ReportDefinitionLocal | null>(null);
  const [dashboardConfig, setDashboardConfig] = useState<DashboardConfig>(DEFAULT_DASHBOARD_CONFIG);
  const [dashboardName, setDashboardName] = useState('Untitled Dashboard');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<'palette' | 'properties'>('palette');
  const [globalFilters, setGlobalFilters] = useState<Record<string, FilterValue | null>>({});
  const [refreshKey, setRefreshKey] = useState(0);

  const selectedWidget = useMemo(
    () => dashboardConfig.widgets.find((w) => w.id === selectedWidgetId) ?? null,
    [dashboardConfig.widgets, selectedWidgetId]
  );

  useEffect(() => {
    if (!isNew && id) {
      loadDashboard(id);
    } else if (isNew && aiDashboardData?.previewConfig) {
      // Pre-populate with AI-generated config
      const config = aiDashboardData.previewConfig;
      setDashboardName(config.name || 'Untitled Dashboard');
      setDashboardDescription(config.description || '');
      if (config.dashboardConfig) {
        // Normalize widget format: AI may return flat x,y,w,h instead of nested layout
        const normalizedConfig = {
          ...config.dashboardConfig,
          widgets: (config.dashboardConfig.widgets || []).map((widget: any) => {
            // If widget has flat x,y,w,h properties, nest them in layout
            if ('x' in widget && !widget.layout) {
              const { x, y, w, h, ...rest } = widget;
              return {
                ...rest,
                layout: { x, y, w, h },
              };
            }
            return widget;
          }),
        };
        setDashboardConfig(normalizedConfig);
      }
      // Mark as AI-generated
      setIsAIGenerated(true);
    }
  }, [id, isNew, aiDashboardData]);

  // Auto-refresh effect - triggers widget data reload at configured interval
  useEffect(() => {
    const seconds = dashboardConfig.autoRefreshSeconds;
    if (!seconds || seconds <= 0 || !isViewMode) return;

    const intervalId = setInterval(() => {
      setRefreshKey((prev) => prev + 1);
      console.log('[SystemDashboardBuilderPage] Auto-refresh triggered');
    }, seconds * 1000);

    return () => clearInterval(intervalId);
  }, [dashboardConfig.autoRefreshSeconds, isViewMode]);

  async function loadDashboard(dashboardId: string) {
    setLoading(true);
    try {
      const data = await getReportDefinition(dashboardId);
      if (data) {
        setDashboard(data);
        setDashboardName(data.name);
        setDashboardDescription(data.description || '');
        
        // Normalize widget format for backwards compatibility
        const rawConfig = data.dashboardConfig || DEFAULT_DASHBOARD_CONFIG;
        const normalizedConfig = {
          ...rawConfig,
          widgets: (rawConfig.widgets || []).map((widget: any) => {
            if ('x' in widget && !widget.layout) {
              const { x, y, w, h, ...rest } = widget;
              return { ...rest, layout: { x, y, w, h } };
            }
            return widget;
          }),
        };
        setDashboardConfig(normalizedConfig);
        
        if (data.name) setBreadcrumbLabel(`/dashboard-templates/${dashboardId}`, data.name);
        
        // Detect if this is an AI-generated template
        const isAITemplate = (data as any).aiGenerationSource === 'ai-generated';
        setIsAIGenerated(isAITemplate);
      }
    } catch (error) {
      console.error('[SystemDashboardBuilderPage] Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const dashboardData = {
        name: dashboardName || 'Untitled Dashboard',
        description: dashboardDescription,
        type: 'dashboard' as const,
        status: dashboard?.status || ('draft' as const),
        dashboardConfig,
        scope: dashboard?.scope || { level: 'system' as const },
        sharing: dashboard?.sharing || { sharedWith: [], isReadOnly: false },
        createdBy: dashboard?.createdBy || 'system-admin',
        updatedBy: 'system-admin',
        // Preserve AI generation marker so it's recognized on reload
        ...(isAIGenerated && { aiGenerationSource: 'ai-generated' as const }),
      };

      if (isNew) {
        const newDashboard = await createReportDefinition(dashboardData as any);
        navigate(`/dashboard-templates/${newDashboard._id}`, { replace: true });
      } else if (id) {
        await updateReportDefinition(id, dashboardData);
      }
    } catch (error) {
      console.error('[SystemDashboardBuilderPage] Failed to save dashboard:', error);
    } finally {
      setSaving(false);
    }
  }, [dashboardName, dashboardDescription, dashboardConfig, dashboard, isNew, id, navigate, isAIGenerated]);

  const handleAddWidget = useCallback((type: WidgetType) => {
    const newWidget: DashboardWidget = {
      id: `widget-${Date.now()}`,
      type,
      title: `New ${WIDGET_TYPES.find((w) => w.type === type)?.label ?? 'Widget'}`,
      layout: { x: 0, y: 0, w: type === 'kpi' ? 3 : 6, h: type === 'kpi' ? 1 : 2 },
    };
    setDashboardConfig((prev) => ({
      ...prev,
      widgets: [...prev.widgets, newWidget],
    }));
    setSelectedWidgetId(newWidget.id);
    setActivePanel('properties');
  }, []);

  const handleDeleteWidget = useCallback((widgetId: string) => {
    setDashboardConfig((prev) => ({
      ...prev,
      widgets: prev.widgets.filter((w) => w.id !== widgetId),
    }));
    if (selectedWidgetId === widgetId) {
      setSelectedWidgetId(null);
    }
  }, [selectedWidgetId]);

  const handleUpdateWidget = useCallback((updates: Partial<DashboardWidget>) => {
    if (!selectedWidgetId) return;
    setDashboardConfig((prev) => ({
      ...prev,
      widgets: prev.widgets.map((w) =>
        w.id === selectedWidgetId ? { ...w, ...updates } : w
      ),
    }));
  }, [selectedWidgetId]);

  const handleFilterChange = useCallback((filterId: string, value: FilterValue | null) => {
    setGlobalFilters((prev) => ({
      ...prev,
      [filterId]: value,
    }));
    setRefreshKey((prev) => prev + 1);
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  // View/Fullscreen mode
  if (isViewMode) {
    return (
      <div className={`h-full flex flex-col bg-gray-50 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        {!isFullscreen && (
          <header className="shrink-0 flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard-templates')} className="p-2 rounded-lg hover:bg-gray-100">
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{dashboardName}</h1>
                {dashboardDescription && <p className="text-sm text-gray-500">{dashboardDescription}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/dashboard-templates/${id}/fullscreen`)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Maximize2 className="h-4 w-4" /> Fullscreen
              </button>
              <button
                onClick={() => navigate(`/dashboard-templates/${id}`)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Edit Dashboard
              </button>
            </div>
          </header>
        )}
        {isFullscreen && (
          <button
            onClick={() => navigate(`/dashboard-templates/${id}/view`)}
            className="fixed top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        <main className={`flex-1 overflow-auto ${isFullscreen ? 'p-4' : 'p-6'}`}>
          <div className="min-h-full rounded-lg bg-white">
            {dashboardConfig.widgets.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                <BarChart3 className="h-12 w-12 mb-3" />
                <p className="text-sm font-medium">No widgets configured</p>
              </div>
            ) : (
              <ReactGridLayout
                className="layout"
                layout={dashboardConfig.widgets.map((w) => ({
                  i: w.id,
                  x: w.layout.x,
                  y: w.layout.y,
                  w: w.layout.w,
                  h: w.layout.h,
                  minW: w.layout.minW ?? 1,
                  minH: w.layout.minH ?? 1,
                  static: true, // Prevent dragging/resizing in view mode
                }))}
                cols={12}
                rowHeight={80}
                width={isFullscreen ? window.innerWidth - 32 : 900}
                isDraggable={false}
                isResizable={false}
              >
                {dashboardConfig.widgets.map((widget) => (
                  <div key={widget.id} className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="p-4 h-full flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        {(() => {
                          const wt = WIDGET_TYPES.find((t) => t.type === widget.type);
                          const Icon = wt?.icon ?? BarChart3;
                          return <Icon className="h-4 w-4 text-gray-600" />;
                        })()}
                        <h4 className="font-medium text-gray-900 text-sm">{widget.title}</h4>
                      </div>
                      <div className="flex-1 min-h-0">
                        <WidgetRenderer
                          widget={widget}
                          refreshKey={refreshKey}
                          onFilterChange={handleFilterChange}
                          filterValues={globalFilters}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </ReactGridLayout>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Edit mode
  return (
    <div className="h-full flex flex-col bg-gray-50">
      <header className="shrink-0 flex items-center justify-between px-4 py-3 border-b bg-white">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard-templates')} className="p-2 rounded-lg hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <input
              type="text"
              value={dashboardName}
              onChange={(e) => setDashboardName(e.target.value)}
              className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
              placeholder="Dashboard Name"
            />
            <input
              type="text"
              value={dashboardDescription}
              onChange={(e) => setDashboardDescription(e.target.value)}
              className="block text-sm text-gray-500 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1 w-full"
              placeholder="Add description..."
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg">
            <RefreshCw className="h-4 w-4" />
            <select
              value={dashboardConfig.autoRefreshSeconds || 0}
              onChange={(e) => setDashboardConfig(prev => ({ ...prev, autoRefreshSeconds: parseInt(e.target.value) }))}
              className="bg-transparent border-none text-sm focus:outline-none cursor-pointer"
            >
              <option value={0}>Auto-refresh: Off</option>
              <option value={30}>30 seconds</option>
              <option value={60}>1 minute</option>
              <option value={300}>5 minutes</option>
              <option value={900}>15 minutes</option>
            </select>
          </div>
          {!isNew && (
            <button
              onClick={() => navigate(`/dashboard-templates/${id}/view`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Play className="h-4 w-4" /> Preview
            </button>
          )}
          {!isNew && (
            <button
              onClick={() => navigate(`/dashboard-templates/${id}/fullscreen`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Full-screen mode for wall displays"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-64 shrink-0 border-r overflow-y-auto bg-white">
          <div className="p-3 border-b">
            <div className="flex gap-1">
              <button
                onClick={() => setActivePanel('palette')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activePanel === 'palette' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <Plus className="h-3 w-3 inline mr-1" /> Widgets
              </button>
              <button
                onClick={() => setActivePanel('properties')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activePanel === 'properties' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                Properties
              </button>
            </div>
          </div>
          {activePanel === 'palette' ? (
            <WidgetPalette onAddWidget={handleAddWidget} />
          ) : (
            <WidgetPropertiesPanel widget={selectedWidget} onUpdate={handleUpdateWidget} />
          )}
        </aside>

        <main className="flex-1 overflow-auto p-6">
          <div className="min-h-full rounded-lg border-2 border-dashed border-gray-300 bg-white">
            {dashboardConfig.widgets.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                <BarChart3 className="h-12 w-12 mb-3" />
                <p className="text-sm font-medium">No widgets yet</p>
                <p className="text-xs mt-1">Click a widget type on the left to add it</p>
              </div>
            ) : (
              <ReactGridLayout
                className="layout"
                layout={dashboardConfig.widgets.map((w) => ({
                  i: w.id,
                  x: w.layout.x,
                  y: w.layout.y,
                  w: w.layout.w,
                  h: w.layout.h,
                  minW: w.layout.minW ?? 1,
                  minH: w.layout.minH ?? 1,
                }))}
                cols={12}
                rowHeight={80}
                width={900}
                onLayoutChange={(newLayout: any) => {
                  setDashboardConfig((prev) => ({
                    ...prev,
                    widgets: prev.widgets.map((w) => {
                      const layoutItem = newLayout.find((l: any) => l.i === w.id);
                      if (layoutItem) {
                        return { ...w, layout: { ...w.layout, x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h } };
                      }
                      return w;
                    }),
                  }));
                }}
                draggableHandle=".drag-handle"
                isResizable
                isDraggable
              >
                {dashboardConfig.widgets.map((widget) => (
                  <div
                    key={widget.id}
                    onClick={() => { setSelectedWidgetId(widget.id); setActivePanel('properties'); }}
                    className={`relative group rounded-lg border-2 transition-all cursor-pointer ${
                      widget.id === selectedWidgetId
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="p-3 h-full">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 drag-handle cursor-grab">
                          <GripVertical className="h-4 w-4 text-gray-400" />
                          {(() => {
                            const wt = WIDGET_TYPES.find((t) => t.type === widget.type);
                            const Icon = wt?.icon ?? BarChart3;
                            return <Icon className="h-4 w-4 text-gray-600" />;
                          })()}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteWidget(widget.id); }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-red-500 transition-opacity"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      <h4 className="font-medium text-gray-900 text-sm truncate">{widget.title}</h4>
                      <p className="text-xs text-gray-500 mt-1">
                        {WIDGET_TYPES.find((t) => t.type === widget.type)?.label ?? widget.type}
                      </p>
                    </div>
                  </div>
                ))}
              </ReactGridLayout>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
